import React, { useState, useEffect, useMemo } from "react"
import AdapterDateFns from "@mui/lab/AdapterDateFns"
import LocalizationProvider from "@mui/lab/LocalizationProvider"
import DatePicker from "@mui/lab/DatePicker"
import { styled } from "@mui/material/styles"
import {
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  TextField,
} from "@mui/material"
import HistoryIcon from "@mui/icons-material/History"
import RefreshIcon from "@mui/icons-material/Refresh"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import moment from "moment"

import AppApi from "../utils/AppApi"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}))

const StatCard = ({ label, value, gradient }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1,
      textAlign: "center",
      background: gradient,
      color: "white",
      borderRadius: 2,
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "0.7rem" }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="bold">
      {value}
    </Typography>
  </Paper>
)

const getDelayInfo = (dn) => {
  if (dn.yard_time === undefined) return { label: "", isDelayed: false, hours: null }
  const eta = moment(dn.eta)
  const ata = moment(dn.yard_time)
  if (eta < ata) {
    const hours = ata.diff(eta, "hours")
    if (hours < 1) {
      const minutes = ata.diff(eta, "minutes")
      return { label: `${minutes} Minutes`, isDelayed: true, hours }
    }
    return { label: `${hours} Hours`, isDelayed: true, hours }
  }
  return { label: "Ontime", isDelayed: false, hours: 0 }
}

const escapeCsvValue = (value) => {
  const str = String(value ?? "")
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

const EtaHistory = (props) => {
  const [viewDate, setViewDate] = useState(new Date())
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const getData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await AppApi.post("/eta_history", {
          view_date: viewDate,
        })
        if (!cancelled) setData(response.data)
      } catch (err) {
        console.error("Error fetching ETA history:", err)
        if (!cancelled) setError("Unable to load ETA history")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    getData()
    return () => {
      cancelled = true
    }
  }, [viewDate])

  const stats = useMemo(() => {
    let ontime = 0
    let delayed = 0
    let pending = 0
    data.forEach((dn) => {
      const { isDelayed } = getDelayInfo(dn)
      if (dn.yard_time === undefined) pending += 1
      else if (isDelayed) delayed += 1
      else ontime += 1
    })
    return { total: data.length, ontime, delayed, pending }
  }, [data])

  const handleExportCsv = () => {
    const headers = ["ETA", "ATA", "Actual Delay", "Truck", "DN"]
    const rows = data.map((dn) => {
      const { label: delayLabel } = getDelayInfo(dn)
      return [
        moment(dn.eta).local().format("YYYY-MM-DD HH:mm"),
        dn.yard_time === undefined
          ? ""
          : moment(dn.yard_time).local().format("YYYY-MM-DD HH:mm"),
        delayLabel,
        dn.truck_no,
        dn.dn,
      ]
    })

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n")

    const blob = new Blob(["﻿" + csvContent], {
      type: "text/csv;charset=utf-8;",
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `eta-history-${moment(viewDate).format("YYYY-MM-DD")}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <HistoryIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                ETA History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ETA vs. actual arrival time by day
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select ETA Date"
                value={viewDate}
                onChange={setViewDate}
                renderInput={(params) => (
                  <TextField {...params} size="small" />
                )}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={loading || data.length === 0}
              sx={{ fontWeight: "bold", px: 2 }}
            >
              Export CSV
            </Button>
            <IconButton
              onClick={() => setViewDate(new Date(viewDate))}
              disabled={loading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          position: "relative",
          minHeight: loading && data.length === 0 ? 200 : undefined,
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 1,
              borderRadius: 3,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {data.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3} md={3}>
              <StatCard
                label="Total DN"
                value={stats.total}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <StatCard
                label="Ontime"
                value={stats.ontime}
                gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <StatCard
                label="Delayed"
                value={stats.delayed}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <StatCard
                label="Not Yet Arrived"
                value={stats.pending}
                gradient="linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)"
              />
            </Grid>
          </Grid>
        )}

        {error && (
          <Paper
            elevation={3}
            sx={{ p: 3, mb: 2, textAlign: "center", backgroundColor: "error.light" }}
          >
            <Typography color="white">{error}</Typography>
          </Paper>
        )}

        {!loading && !error && data.length === 0 && (
          <Paper elevation={0} sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">
              No ETA records for this date
            </Typography>
          </Paper>
        )}

        {data.length > 0 && (
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="eta history table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>ETA</StyledTableCell>
                  <StyledTableCell>ATA</StyledTableCell>
                  <StyledTableCell align="center">Actual Delay</StyledTableCell>
                  <StyledTableCell>Truck</StyledTableCell>
                  <StyledTableCell>DN</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((dn, index) => {
                  const { label: delayLabel, isDelayed } = getDelayInfo(dn)
                  return (
                    <StyledTableRow key={`${dn.dn}-${dn.truck_no}-${index}`}>
                      <StyledTableCell>
                        <Typography fontWeight="bold" variant="body2">
                          {moment(dn.eta).local().format("HH:mm")}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {dn.yard_time === undefined
                          ? ""
                          : moment(dn.yard_time).local().format("DD/MM HH:mm")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dn.yard_time === undefined ? (
                          ""
                        ) : (
                          <Chip
                            color={isDelayed ? "error" : "success"}
                            variant={isDelayed ? "filled" : "outlined"}
                            size="small"
                            label={delayLabel}
                            sx={{ fontWeight: "bold" }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell>{dn.truck_no}</StyledTableCell>
                      <StyledTableCell>{dn.dn}</StyledTableCell>
                    </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  )
}

export default EtaHistory
